import * as React from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "../../../components/mui";
import Modal from "../../../components/shared/Modal";
import { useToast } from "../../../components/shared/ToastProvider";

/**
 * InventoryFormModal
 * Reusable modal for creating/updating inventory items.
 * UI-only; backend will wire form state + submission later.
 */
export default function InventoryFormModal({
  open,
  onClose,
  mode = "add", // "add" | "edit"
  initialValues = {}
}) {
  const isEdit = mode === "edit";
  const { showToast } = useToast();

  const handlePrimaryClick = () => {
    showToast({
      message: isEdit ? "Inventory item updated" : "Inventory item created",
      severity: "success"
    });
    if (onClose) {
      onClose();
    }
  };

  const actions = (
    <>
      <Button variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="contained" onClick={handlePrimaryClick}>
        {isEdit ? "Update" : "Create"}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Condo Unit Inventory" : "Add Condo Unit Inventory"}
      actions={actions}
      maxWidth="sm"
    >
      <Box className="modal-form-grid" style={{ marginTop: 8 }}>
        {/* Room */}
        <TextField
          fullWidth
          size="small"
          className="modal-form-row"
          label="Room"
          defaultValue={initialValues.room || ""}
        />

        {/* Item */}
        <TextField
          fullWidth
          size="small"
          className="modal-form-row"
          label="Item"
          defaultValue={initialValues.item || ""}
        />

        {/* Owned By (dropdown) */}
        <FormControl fullWidth size="small" className="modal-form-row">
          <InputLabel>Owned By</InputLabel>
          <Select
            defaultValue={initialValues.ownedBy || "Company"}
            label="Owned By"
          >
            <MenuItem value="Company">Company</MenuItem>
            <MenuItem value="Employee">Employee</MenuItem>
            <MenuItem value="Condo">Condo</MenuItem>
          </Select>
        </FormControl>

        {/* Purchased (date) & Qty */}
        <Box className="modal-form-row modal-form-row--two-cols">
          <TextField
            fullWidth
            size="small"
            label="Purchased"
            type="date"
            defaultValue={initialValues.purchasedRaw || ""}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Qty"
            defaultValue={initialValues.qty || ""}
          />
        </Box>

        {/* Currency, Unit Price & Total Price - one line */}
        <Box className="modal-form-row modal-form-row--three-cols">
          <FormControl fullWidth required size="small">
            <InputLabel>Currency</InputLabel>
            <Select
              defaultValue={initialValues.currency || "PHP"}
              label="Currency"
            >
              <MenuItem value="PHP">PHP</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="KHR">KHR</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Unit Price"
            placeholder="0.00"
            defaultValue={initialValues.unitPriceValue || ""}
            inputProps={{ step: "0.01" }}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Total Price"
            placeholder="0.00"
            defaultValue={initialValues.totalValue || ""}
            inputProps={{ step: "0.01" }}
          />
        </Box>

        {/* Remarks */}
        <TextField
          fullWidth
          multiline
          rows={3}
          size="small"
          className="modal-form-row"
          label="Remarks"
          defaultValue={initialValues.remarks || ""}
        />

        {/* Status under Remarks */}
        <FormControl fullWidth size="small" className="modal-form-row">
          <InputLabel>Status</InputLabel>
          <Select
            defaultValue={initialValues.status || "Brand New"}
            label="Status"
          >
            <MenuItem value="Brand New">Brand New</MenuItem>
            <MenuItem value="Good">Good</MenuItem>
            <MenuItem value="For Repair">For Repair</MenuItem>
            <MenuItem value="Disposed">Disposed</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Modal>
  );
}
